import React, { useState, useEffect } from 'react';
import { Users, X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { normalizeAssetUrl } from '../utils/assetUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersSection = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`);
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const openMedia = (index) => {
    setCurrentIndex(index);
    setSelectedMedia(customers[index]);
  };

  const closeMedia = () => setSelectedMedia(null);

  const nextMedia = () => {
    const newIndex = (currentIndex + 1) % customers.length;
    setCurrentIndex(newIndex);
    setSelectedMedia(customers[newIndex]);
  };

  const prevMedia = () => {
    const newIndex = (currentIndex - 1 + customers.length) % customers.length;
    setCurrentIndex(newIndex);
    setSelectedMedia(customers[newIndex]);
  };

  return (
    <section id="customers" className="py-20 bg-stone-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-amber-500 font-semibold text-sm tracking-wider uppercase">Nossos Clientes</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-2 mb-4">
            <span className="text-amber-500">Clientes Satisfeitos</span> em Ação
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto">
            Veja fotos e vídeos dos nossos clientes curtindo suas rotativas! 📸🥩
          </p>
        </div>

        {/* Customer Gallery Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : customers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {customers.map((item, index) => (
              <div
                key={item.id}
                onClick={() => openMedia(index)}
                className="relative aspect-square bg-stone-800 rounded-xl overflow-hidden cursor-pointer group"
              >
                {item.type === 'video' ? (
                  <>
                    <video src={normalizeAssetUrl(item.url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-stone-900 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <img src={normalizeAssetUrl(item.url)} alt={item.caption || "Cliente"} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  </>
                )}
                
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                  {item.customerName && <p className="text-white text-sm font-medium">{item.customerName}</p>}
                  {item.location && <p className="text-amber-400 text-xs">{item.location}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-10 h-10 text-stone-600" />
            </div>
            <p className="text-stone-400">Em breve fotos dos nossos clientes!</p>
          </div>
        )}

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-2xl md:text-3xl font-bold text-amber-500">500+</p>
            <p className="text-stone-400 text-sm">Clientes Satisfeitos</p>
          </div>
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-2xl md:text-3xl font-bold text-amber-500">27</p>
            <p className="text-stone-400 text-sm">Estados Atendidos</p>
          </div>
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-2xl md:text-3xl font-bold text-amber-500">10+</p>
            <p className="text-stone-400 text-sm">Anos de Experiência</p>
          </div>
          <div className="bg-stone-800/50 rounded-xl p-4">
            <p className="text-2xl md:text-3xl font-bold text-amber-500">100%</p>
            <p className="text-stone-400 text-sm">Satisfação</p>
          </div>
        </div>

        {/* Media Modal */}
        {selectedMedia && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
            <button onClick={closeMedia} className="absolute top-4 right-4 text-white hover:text-amber-500 transition-colors z-10">
              <X className="w-8 h-8" />
            </button>
            
            {customers.length > 1 && (
              <>
                <button onClick={prevMedia} className="absolute left-4 text-white hover:text-amber-500 transition-colors"><ChevronLeft className="w-10 h-10" /></button>
                <button onClick={nextMedia} className="absolute right-4 text-white hover:text-amber-500 transition-colors"><ChevronRight className="w-10 h-10" /></button>
              </>
            )}

            <div className="max-w-4xl w-full">
              {selectedMedia.type === 'video' ? (
                <video src={normalizeAssetUrl(selectedMedia.url)} className="w-full rounded-lg" controls autoPlay playsInline />
              ) : (
                <img src={normalizeAssetUrl(selectedMedia.url)} alt={selectedMedia.caption || "Cliente"} className="w-full max-h-[80vh] object-contain rounded-lg" />
              )}
              <div className="text-center mt-4">
                {selectedMedia.customerName && <p className="text-white font-medium">{selectedMedia.customerName}</p>}
                {selectedMedia.caption && <p className="text-stone-400">{selectedMedia.caption}</p>}
                {selectedMedia.location && <span className="text-amber-400 text-sm">{selectedMedia.location}</span>}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomersSection;
